import * as THREE from 'three';
import { detectGearRegions } from './gearDetection';
import { easeOutCubic, tween } from './tween';
import type { ProtectSettings } from '../types/protect';

/** Shared across every patched material so the overlay can be toggled in one place. */
export const protectUniforms = {
  uProtectShow: { value: 0 },
  uProtectColor: { value: new THREE.Color('#1a73e8') },
  uSoftColor: { value: new THREE.Color('#7fb6f5') },
  uEraseColor: { value: new THREE.Color('#e53935') }
};

export function patchProtectMaterial(material: THREE.Material | THREE.Material[]) {
  (Array.isArray(material) ? material : [material]).forEach((mat) => {
    if (mat.userData.protectPatched) return;
    mat.userData.protectPatched = true;
    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, protectUniforms);
      shader.vertexShader = shader.vertexShader.
      replace('#include <common>', '#include <common>\nattribute float protect;\nattribute float eraseFx;\nvarying float vProtect;\nvarying float vErase;').
      replace('#include <begin_vertex>', '#include <begin_vertex>\nvProtect = protect;\nvErase = eraseFx;');
      shader.fragmentShader = shader.fragmentShader.
      replace(
        '#include <common>',
        '#include <common>\nuniform float uProtectShow;\nuniform vec3 uProtectColor;\nuniform vec3 uSoftColor;\nuniform vec3 uEraseColor;\nvarying float vProtect;\nvarying float vErase;'
      ).
      replace(
        '#include <opaque_fragment>',
        `#include <opaque_fragment>
          if (uProtectShow > 0.5) {
            float p = clamp(vProtect, 0.0, 1.0);
            vec3 tint = mix(uSoftColor, uProtectColor, smoothstep(0.55, 0.95, p));
            gl_FragColor.rgb = mix(gl_FragColor.rgb, tint, clamp(p * 2.0, 0.0, 1.0) * 0.72);
            gl_FragColor.rgb = mix(gl_FragColor.rgb, uEraseColor, clamp(vErase, 0.0, 1.0) * 0.78);
          }`
      );
    };
    mat.customProgramCacheKey = () => 'pf-protect';
    mat.needsUpdate = true;
  });
}

type Entry = {
  mesh: THREE.Mesh;
  protect: THREE.BufferAttribute;
  erase: THREE.BufferAttribute;
  saved: Float32Array;
  eraseActive: Set<number>;
};

type Stamp = {point: THREE.Vector3;normal: THREE.Vector3;};

const Z = new THREE.Vector3(0, 0, 1);
const ERASE_FADE = 0.45;

export class ProtectPainter {
  private entries: Entry[] = [];
  private cursor = new THREE.Group();
  private ringMaterial = new THREE.MeshBasicMaterial({ color: '#1a73e8', transparent: true, opacity: 0.95, depthTest: false, side: THREE.DoubleSide });
  private discMaterial = new THREE.MeshBasicMaterial({ color: '#1a73e8', transparent: true, opacity: 0.12, depthTest: false, side: THREE.DoubleSide });
  private settings: ProtectSettings = { tool: 'paint', brushSize: 5, mode: 'protected' };
  private pending: Stamp[] = [];
  private last: THREE.Vector3 | null = null;
  private inv = new THREE.Matrix4();

  constructor(private model: THREE.Object3D, private scene: THREE.Scene) {
    model.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const g = mesh.geometry;
      if (!g.attributes.normal) g.computeVertexNormals();
      if (!g.boundingBox) g.computeBoundingBox();
      const count = g.attributes.position.count;
      let protect = g.getAttribute('protect') as THREE.BufferAttribute | undefined;
      let erase = g.getAttribute('eraseFx') as THREE.BufferAttribute | undefined;
      if (!protect) {
        protect = new THREE.BufferAttribute(new Float32Array(count), 1).setUsage(THREE.DynamicDrawUsage);
        g.setAttribute('protect', protect);
      }
      if (!erase) {
        erase = new THREE.BufferAttribute(new Float32Array(count), 1).setUsage(THREE.DynamicDrawUsage);
        g.setAttribute('eraseFx', erase);
      }
      patchProtectMaterial(mesh.material);
      if (mesh.userData.original) patchProtectMaterial(mesh.userData.original);
      this.entries.push({ mesh, protect, erase, saved: new Float32Array(protect.array as Float32Array), eraseActive: new Set() });
    });

    const ring = new THREE.Mesh(new THREE.RingGeometry(0.9, 1, 64), this.ringMaterial);
    const disc = new THREE.Mesh(new THREE.CircleGeometry(0.9, 48), this.discMaterial);
    ring.renderOrder = disc.renderOrder = 999;
    this.cursor.add(disc, ring);
    this.cursor.visible = false;
    scene.add(this.cursor);
  }

  configure(settings: ProtectSettings) {
    this.settings = settings;
    const color = settings.tool === 'erase' ? '#e53935' : settings.mode === 'soft' ? '#4b95ec' : '#1a73e8';
    this.ringMaterial.color.set(color);
    this.discMaterial.color.set(color);
  }

  setVisible(visible: boolean) {
    protectUniforms.uProtectShow.value = visible ? 1 : 0;
    if (!visible) this.cursor.visible = false;
  }

  hover(hit: THREE.Intersection | null) {
    if (!hit || !hit.face) {
      this.cursor.visible = false;
      return;
    }
    const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
    this.cursor.visible = true;
    this.cursor.position.copy(hit.point).addScaledVector(normal, 0.15);
    this.cursor.quaternion.setFromUnitVectors(Z, normal);
    this.cursor.scale.setScalar(this.settings.brushSize);
  }

  strokeStart(hit: THREE.Intersection) {
    this.last = null;
    this.strokeTo(hit);
  }

  strokeTo(hit: THREE.Intersection) {
    if (!hit.face) return;
    const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
    const point = hit.point.clone();
    if (this.last) {
      const step = this.settings.brushSize * 0.35;
      const steps = Math.min(24, Math.ceil(this.last.distanceTo(point) / step));
      for (let s = 1; s <= steps; s++) this.pending.push({ point: this.last.clone().lerp(point, s / steps), normal });
    } else {
      this.pending.push({ point, normal });
    }
    this.last = point;
  }

  strokeEnd() {
    this.last = null;
  }

  update(dt: number) {
    if (this.pending.length) {
      this.applyBrush(this.pending);
      this.pending = [];
    }
    for (const e of this.entries) {
      if (e.eraseActive.size === 0) continue;
      const arr = e.erase.array as Float32Array;
      e.eraseActive.forEach((i) => {
        arr[i] -= dt / ERASE_FADE;
        if (arr[i] <= 0) {
          arr[i] = 0;
          e.eraseActive.delete(i);
        }
      });
      e.erase.needsUpdate = true;
    }
  }

  private applyBrush(stamps: Stamp[]) {
    const { tool, brushSize, mode } = this.settings;
    const value = mode === 'soft' ? 0.5 : 1;
    const v = new THREE.Vector3();
    const nv = new THREE.Vector3();
    for (const e of this.entries) {
      const m = e.mesh.matrixWorld;
      this.inv.copy(m).invert();
      const scale = new THREE.Vector3().setFromMatrixScale(m).x || 1;
      const r = brushSize / scale;
      const r2 = r * r;
      const local = stamps.map((s) => ({ p: s.point.clone().applyMatrix4(this.inv), n: s.normal.clone().transformDirection(this.inv) }));
      const bb = e.mesh.geometry.boundingBox;
      const near = bb ? local.filter((s) => bb.distanceToPoint(s.p) <= r) : local;
      if (near.length === 0) continue;

      const pos = e.mesh.geometry.attributes.position;
      const nor = e.mesh.geometry.attributes.normal;
      const arr = e.protect.array as Float32Array;
      const er = e.erase.array as Float32Array;
      let changed = false;
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        for (const s of near) {
          if (v.distanceToSquared(s.p) > r2) continue;
          if (nor && nv.fromBufferAttribute(nor, i).dot(s.n) < -0.1) continue;
          if (tool === 'paint') {
            if (arr[i] !== value) {
              arr[i] = value;
              changed = true;
            }
          } else if (arr[i] > 0) {
            arr[i] = 0;
            er[i] = 1;
            e.eraseActive.add(i);
            changed = true;
          }
          break;
        }
      }
      if (changed) {
        e.protect.needsUpdate = true;
        e.erase.needsUpdate = true;
      }
    }
  }

  get cursorObject(): THREE.Object3D {
    return this.cursor;
  }

  /** Copy of the saved (committed) protection for history snapshots. */
  snapshot(): Float32Array[] {
    return this.entries.map((e) => new Float32Array(e.saved));
  }

  /** Replaces both current and saved protection with a snapshot. */
  load(arrays: Float32Array[]) {
    this.entries.forEach((e, i) => {
      const src = arrays[i];
      const arr = e.protect.array as Float32Array;
      if (src && src.length === arr.length) arr.set(src);else
      arr.fill(0);
      e.saved.set(arr);
      (e.erase.array as Float32Array).fill(0);
      e.eraseActive.clear();
      e.protect.needsUpdate = e.erase.needsUpdate = true;
    });
  }

  /** Runs `fn` with the saved protection temporarily applied, then restores unsaved edits. */
  withSaved<T>(fn: () => T): T {
    const current = this.entries.map((e) => new Float32Array(e.protect.array as Float32Array));
    this.entries.forEach((e) => (e.protect.array as Float32Array).set(e.saved));
    try {
      return fn();
    } finally {
      this.entries.forEach((e, i) => (e.protect.array as Float32Array).set(current[i]));
    }
  }

  isDirty(): boolean {
    return this.entries.some((e) => {
      const arr = e.protect.array as Float32Array;
      for (let i = 0; i < arr.length; i++) if (arr[i] !== e.saved[i]) return true;
      return false;
    });
  }

  save() {
    this.entries.forEach((e) => e.saved.set(e.protect.array as Float32Array));
  }

  revert() {
    this.entries.forEach((e) => {
      (e.protect.array as Float32Array).set(e.saved);
      (e.erase.array as Float32Array).fill(0);
      e.eraseActive.clear();
      e.protect.needsUpdate = e.erase.needsUpdate = true;
    });
  }

  clearAll() {
    this.entries.forEach((e) => {
      (e.protect.array as Float32Array).fill(0);
      e.protect.needsUpdate = true;
    });
  }

  /** Runs the simulated AI selection and fades the detected regions in. Returns the region count. */
  async aiSelect(isCancelled: () => boolean): Promise<number> {
    this.model.updateMatrixWorld(true);
    const { indices, regions } = detectGearRegions(this.entries.map((e) => e.mesh));
    const value = this.settings.mode === 'soft' ? 0.5 : 1;
    const from = this.entries.map((e) => new Float32Array(e.protect.array as Float32Array));
    await tween(
      550,
      (k) => {
        const t = easeOutCubic(k) * value;
        this.entries.forEach((e, ei) => {
          const arr = e.protect.array as Float32Array;
          indices[ei].forEach((i) => arr[i] = Math.max(from[ei][i], t));
          e.protect.needsUpdate = true;
        });
      },
      isCancelled
    );
    return regions;
  }

  dispose() {
    this.scene.remove(this.cursor);
    this.cursor.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
    this.ringMaterial.dispose();
    this.discMaterial.dispose();
  }
}
